import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { MessagingService } from "../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../../dynamic-forms/inputs/selectable-input.model";
import { TextboxType } from "../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { Measures } from "../models/measures.model";
import { IOverreadModel } from "../overread.model";
import { IRateModel } from "../rates.model";

interface IMeasureForm {
  formGroup: FormGroup;
  models: DynamicInput[];
}

enum MeasureInputs {
  MEASURE = "measure",
}

enum FormInputs {
  COMPLIANT = "compliant",
  PARTIAL_COMPLIANT = "partiallycompliant",
  NON_COMPLIANT = "noncompliant",
  EXCLUSION = "noncompliantexclusion",
}

@Component({
             selector: "app-measure-sampling-overread",
             templateUrl: "./measure-sampling-overread.component.html",
             styleUrls: ["./measure-sampling-overread.component.scss"],
           })
export class MeasureSamplingOverreadComponent implements OnInit {

  @Input() set measureList(list: Measures[]) {
    this.measureOptions = [...list.map(i => new SelectableInput({text: i.measureCode, value: i.measureCode}))];
  }

  @Input() set overreadInfo(info: IOverreadModel) {
    if (!ArrayHelper.isAvailable(info?.overread?.rates?.rate) || !ArrayHelper.isAvailable(info?.overread2?.rates?.rate)) {
      this.buildRow();
      return;
    }
    this.measureFormOverread = [];
    this.measureFormOverread2 = [];
    const overread = this.filterByMeasureCode(info.overread.rates.rate);
    const overread2 = this.filterByMeasureCode(info.overread2.rates.rate);
    const groupedByMeasureCodeOR1 = this.groupByProperty(overread, "measurecode");
    const groupedByMeasureCodeOR2 = this.groupByProperty(overread2, "measurecode");
    this.createRows(groupedByMeasureCodeOR1, "OR1");
    this.createRows(groupedByMeasureCodeOR2, "OR2");
  }

  measureFormOverread: IMeasureForm[] = [];
  measureFormOverread2: IMeasureForm[] = [];
  measureOptions: SelectableInput[] = [];

  constructor(
    private readonly formService: FormService,
    private messagingService: MessagingService) {
  }

  ngOnInit(): void {
  }

  trackByIndex(index: number): number {
    return index;
  }

  addMeasure(): void {
    this.buildRow();
  }

  getControls(form: FormGroup): AbstractControl[] {
    const controls: AbstractControl[] = [];
    for (const [key, control] of Object.entries(form.controls)) {
      controls.push(control);
    }
    return controls;
  }

  deleteRow(index: number): void {
    this.measureFormOverread.splice(index, 1);
    this.measureFormOverread2.splice(index, 1);
  }

  getFormattedValues(form: IMeasureForm[], measureCodes: string[] = []): any[] {
    const rates = [];
    form.forEach((measure, index) => {
      const inputs = Object.values(FormInputs);
      inputs.forEach(input => {
        rates.push({
                     ratetype: input,
                     value: measure.formGroup.get(input)?.value || 0,
                     measurecode: measureCodes[index],
                     active: 1,
                   });
      });
    });
    return rates;
  }

  onInputChange(event: { value: string }): void {
    if (!StringHelper.isAvailable(event?.value)) {
      return;
    }
    if (this.hasDuplicates(this.measureFormOverread, event.value)) {
      this.messagingService.showToast("Measures cannot have duplicates", SeverityType.ERROR);
    }
  }

  formHasErrors(): boolean {
    if (!this.isDefaultRow()) {
      return false;
    }
    return this.measureFormOverread.some(form => form.formGroup.invalid) || this.measureFormOverread2.some(form => form.formGroup.invalid);
  }

  formHasMissingMeasure(): boolean {
    return this.measureFormOverread.some(
      form => !StringHelper.isAvailable(form.formGroup.get(MeasureInputs.MEASURE)?.value as string)
    );
  }

  formHasDuplicates(): boolean {
    const measures = this.measureFormOverread.map(m => m.formGroup.get(MeasureInputs.MEASURE)?.value as string);
    const duplicates = new Set(measures);
    return measures?.length !== duplicates?.size;
  }

  getValuesAsObject(type = "OR1"): any[] {
    if (!this.isDefaultRow()) {
      return [];
    }
    const measureCodes = [...this.measureFormOverread.map(m => m.formGroup.get(MeasureInputs.MEASURE)?.value as string)];
    if (type === "OR1") {
      return [...this.getFormattedValues(this.measureFormOverread, measureCodes)];
    }
    return [...this.getFormattedValues(this.measureFormOverread2, measureCodes)];
  }

  private isDefaultRow(): boolean {
    return !(this.measureFormOverread?.length === 1 && !StringHelper.isAvailable(this.measureFormOverread[0].formGroup.get(MeasureInputs.MEASURE)?.value));
  }

  private buildRow(): void {
    const models = [
      this.createDropdown("measure", ""),
      this.createTextbox("compliant", "", 0),
      this.createTextbox("partiallycompliant", "", 0),
      this.createTextbox("noncompliant", "", 0),
      this.createTextbox("noncompliantexclusion", "", 0),
    ];
    const modelsOverread2 = [
      this.createTextbox("compliant", "", 0),
      this.createTextbox("partiallycompliant", "", 0),
      this.createTextbox("noncompliant", "", 0),
      this.createTextbox("noncompliantexclusion", "", 0),
    ];
    const formGroup = this.formService.createFormGroup(models);
    const formGroupOverread2 = this.formService.createFormGroup(modelsOverread2);
    this.measureFormOverread = [...this.measureFormOverread, {formGroup, models}];
    this.measureFormOverread2 = [...this.measureFormOverread2, {
      formGroup: formGroupOverread2,
      models: modelsOverread2,
    }];

    this.measureFormOverread[this.measureFormOverread.length - 1].formGroup.patchValue(
      {
        compliant: 0,
        partiallycompliant: 0,
        noncompliant: 0,
        noncompliantexclusion: 0,
      });

    this.measureFormOverread2[this.measureFormOverread2.length - 1].formGroup.patchValue(
      {
        compliant: 0,
        partiallycompliant: 0,
        noncompliant: 0,
        noncompliantexclusion: 0,
      });
  }

  revertValues(index: number): void {
    const patchValuesOR1 = {
      measure: "",
      compliant: 0,
      partiallycompliant: 0,
      noncompliant: 0,
      noncompliantexclusion: 0,
    };
    const patchValuesOR2 = {
      compliant: 0,
      partiallycompliant: 0,
      noncompliant: 0,
      noncompliantexclusion: 0,
    };
    this.measureFormOverread[index].formGroup.patchValue(patchValuesOR1);
    this.measureFormOverread2[index].formGroup.patchValue(patchValuesOR2);
  }

  private createTextbox(key: string, label: string, value = 0): Textbox {
    return new Textbox(
      {
        key,
        label,
        value,
        validators: [Validators.min(0), Validators.max(100), Validators.required],
        type: TextboxType.NUMBER,
        errorMessages: {
          required: "A value is required.",
          min: "Enter a value above 0",
          max: "Enter a value below 100",
        },
      });
  }

  private createDropdown(key: string, label: string, value?: string): Dropdown {
    return new Dropdown(
      {
        key,
        label,
        value,
        options: this.measureOptions,
        placeholder: "Measure...",
        validators: [Validators.required],
        errorMessages: {
          required: "A measure is required.",
        },
      });
  }

  private groupByProperty(arr: any[], prop: string): {[key: string]: any} {
    return arr.reduce((acc, currentItem) => {
      const key = currentItem[prop];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(currentItem);
      return acc;
    },                {});
  }

  private createRows(obj: { [key: string]: any }, type: "OR1" | "OR2" = "OR1"): void {
    Object.keys(obj).forEach((key, index) => {
      const models = [];
      const valuesToPatch = {};
      if (type === "OR1") {
        models.push(this.createDropdown(MeasureInputs.MEASURE, ""));
        valuesToPatch[MeasureInputs.MEASURE] = key;
      }
      obj[key].forEach((element: { ratetype: string; value: number }) => {
        models.push(this.createTextbox(element.ratetype, ""));
        valuesToPatch[element.ratetype] = element.value;
      });
      const formGroup = this.formService.createFormGroup(models);
      if (type === "OR1") {
        this.measureFormOverread = [...this.measureFormOverread, {formGroup, models}];
        this.measureFormOverread[index].formGroup.patchValue(valuesToPatch);
        return;
      }
      this.measureFormOverread2 = [...this.measureFormOverread2, {formGroup, models}];
      this.measureFormOverread2[index].formGroup.patchValue(valuesToPatch);
    });
  }

  private filterByMeasureCode(arr: IRateModel[]): IRateModel[] {
    return  arr.filter(value => StringHelper.isAvailable(value?.measurecode) && this.measureOptions?.find(m => m.text === value?.measurecode));
  }

  private hasDuplicates(arr: IMeasureForm[], value: string): boolean {
    const findings = arr.filter(measureForm => measureForm.formGroup.get(MeasureInputs.MEASURE)?.value === value);
    return ArrayHelper.isAvailable(findings) && findings.length > 1;
  }

}
